import React from 'react';

import { track, useAutoNavigate, useFlowState } from '@betterment/js-runtime';
import { Box, IconButton, Link, Logo, PancakeStack, Text, WorkflowHeader } from '@betterment/samba-react';

import { trackClick } from '../../features/track';
import brochureUrl from '../../Shared/brochureUrl';
import { exitIntentConfirmation } from '../../Shared/exitIntent';
import { getRelevantStepsSequence } from '../../Shared/getRelevantStepsSequence';
import { useTransitionAnnouncement } from '../../Shared/hooks/useTransitionAnnouncement';
import { locationAssign } from '../../Shared/path-helpers';

export type Props = {
  splitStepsAt?: string[];
  excludedSteps?: string[];
};

const SharedSignupFlowHeader: React.FC<Props> = ({ splitStepsAt = [], excludedSteps = [] }) => {
  const { goToPreviousStep, flowState } = useFlowState();
  const navigateRouter = useAutoNavigate();
  const flowSteps = getRelevantStepsSequence({
    steps: flowState.steps as string[],
    splitStepsAt,
    currentStep: flowState.currentStep,
    excludedSteps,
  });

  const steps = flowSteps.length;
  const getCurrentStep = () => flowSteps.findIndex(step => step === flowState.currentStep) + 1;
  const currentStep = getCurrentStep();
  const stepAnnouncement = useTransitionAnnouncement(`currently on step ${currentStep} of ${steps}`);

  const closeHandler = () => {
    track('ElementClicked', { name: 'exitFlow', flow: 'SignupFlow' });
    if (currentStep === 1) {
      locationAssign(brochureUrl('/getstarted'));
    } else {
      void navigateRouter(flowState.basePath);
    }
  };

  const backButtonHandler = () => {
    track('ElementClicked', { name: 'previousStep', flow: 'SignupFlow' });
    goToPreviousStep();
  };

  const exitIntent = () => {
    if (currentStep === 1) {
      closeHandler();
    } else {
      exitIntentConfirmation({ callback: () => closeHandler() });
    }
  };

  return (
    <PancakeStack.Header>
      <WorkflowHeader>
        <WorkflowHeader.Leading>
          {currentStep === 1 ? (
            <Link
              href={brochureUrl('/')}
              aria-label="Betterment logo - Return to Betterment.com"
              onClick={() => trackClick({ name: 'exitFlow', flow: 'SignupFlow' })}
              variant="ghostPrimary"
            >
              <Logo aria-hidden="true" type="signature" css={{ height: 'size.300', flexShrink: 0 }} />
            </Link>
          ) : (
            <IconButton
              name="LeftArrowSmall"
              label={`Back to step ${currentStep - 1} of ${steps}`}
              onClick={backButtonHandler}
            />
          )}
        </WorkflowHeader.Leading>
        <WorkflowHeader.Title>
          {/* Accessibility Note: This is a persistent live region used to announce page/step changes to screen readers */}
          <Box
            role="status"
            style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', overflow: 'hidden' }}
            aria-atomic="true"
            id="a11y-status"
          >
            {stepAnnouncement}
          </Box>
          <Text aria-label={stepAnnouncement}>
            <Box as="span" aria-hidden={true}>
              {currentStep} of {steps}
            </Box>
          </Text>
        </WorkflowHeader.Title>
        <WorkflowHeader.Trailing>
          <IconButton
            name="X"
            label={currentStep === 1 ? 'Return to Betterment.com/getstarted' : 'Exit to step 1'}
            onClick={exitIntent}
          />
        </WorkflowHeader.Trailing>
      </WorkflowHeader>
    </PancakeStack.Header>
  );
};

export default SharedSignupFlowHeader;
